<script setup lang="ts">
import { mdiChevronUp, mdiHeart, mdiHeartOutline, mdiPlaylistPlus } from "@mdi/js";
import NavIcon from "../../Nav/NavIcon.vue";
import { IEvent } from "~/types/sports/event";
import { useAuth } from "~/store/auth";
import { useBetbook } from "~/store/betbook";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import { useSettings } from "~/store/settings";

interface Props {
	event: IEvent;
	defaultValue?: boolean;
}

const props = defineProps<Props>();

const auth = useAuth();
const store = useBetbook();
const router = useRouter();
const route = useRoute();
const settings = useSettings();

const handleFav = (id: string, region: string, sportId: string, title: string) => {
	if (!auth.loggedIn) {
		if (settings.isNativeApp) return window.promptLogin();
		auth.modals.login = true;
	} else if (store.isFavourite(id, "League")) {
		store.removeFavourite(id, "League");
	} else {
		store.addFavourite({ id, region, sportId, title, favouriteType: "League" });
	}
};

const handleLeagueClick = (event: IEvent) => {
	router.push({
		path: sportUrlBuilder({ fixtureType: "highlights", sport: event.sportId }),
		query: {
			...route.query,
			selectedLeagues: `${event.regionId}_${event.leagueId}`,
		},
	});
	store.filters.regionAndLeagueIds = [{ regionId: event.regionId, leagueId: event.leagueId }];
};
</script>

<template>
	<details :open="defaultValue">
		<summary>
			<div
				:class="[
					'event-accordion-summary-header flex items-center rounded-lg relative w-full cursor-pointer h-8 p-1 text-xs gap-1 font-bold  bg-dark-600 text-light-50 transition-all whitespace-nowrap flex-shrink-0',
				]"
			>
				<div
					v-if="auth.loggedIn && settings.hasFeature('IsLeagueFavouriteEnabled')"
					@click="
						(e) => {
							e.preventDefault();
							e.stopPropagation();
							handleFav(`${event.regionId}_${event.leagueId}`, event.regionId, event.sportId, event.league);
						}
					"
					class="flex items-center justify-center flex-shrink-0 rounded-md w-7 h-7 bg-dark-600"
				>
					<UiIcon
						:path="store.isFavourite(`${event.regionId}_${event.leagueId}`, 'League') ? mdiHeart : mdiHeartOutline"
						:class="['w-6 h-6', store.isFavourite(`${event.regionId}_${event.leagueId}`, 'League') ? 'fill-identity' : 'fill-light-50']"
					/>
				</div>
				<DataFreeGuard>
					<NavIcon
						:width="24"
						:height="24"
						:src="`/icons/regions-leagues-flags/${event.regionId.toLowerCase()}-circular.svg`"
						:alt="event.region"
						fallback="/icons/regions-leagues-flags/default-circular.svg"
						class="flex-shrink-0"
					/>
				</DataFreeGuard>
				<span class="overflow-hidden text-ellipsis"> {{ event.league }}, {{ event.region }} </span>
				<div class="pr-7">
					<div
						@click="
							(e) => {
								e.preventDefault();
								e.stopPropagation();
								handleLeagueClick(event);
							}
						"
						class="flex items-center justify-center flex-shrink-0 rounded-md w-7 h-7 bg-dark-600"
					>
						<UiIcon :path="mdiPlaylistPlus" class="w-6 h-6 fill-light-50" />
					</div>
				</div>
				<div class="absolute -translate-y-1/2 right-2 top-1/2">
					<UiIcon :class="['z-10 w-6 h-6 fill-light-50 transition-all event-accordion-icon']" :path="mdiChevronUp" />
				</div>
			</div>
		</summary>

		<slot />
	</details>
</template>

<style lang="scss">
details[open] {
	.event-accordion-summary-header {
		@apply rounded-t-lg rounded-none;
	}
	.event-accordion-icon {
		@apply -rotate-180;
	}
}
</style>
